import { useCallback, useEffect, useMemo, useState } from 'react';
import { Row } from 'react-bootstrap';
import { TitleGroupComponent } from 'components/title/title-group-component';
import { Footer } from 'components/footer/footer-component';
import { useLoader } from 'components/loader/loader-context';
import { UseAlert } from 'hook/alert-hook';
import { UseAuth } from 'hook/auth-hook';
import { getEnv } from 'helper/window-helper';
import { showAlertPorStatus, showErrorAlert, showSuccessAlert, showWaitAlert } from 'helper/show-alert-helper';
import { ParseDateToEn } from 'helper/format-date-helper';
import {
  resolverMensagemErro,
  filtrarPorPeriodo,
  geracaoLiberadaApos,
  isConflito,
  podeBaixarCsv,
  MENSAGEM_BLOQUEIO_PADRAO,
  MENSAGEM_CSV_BLOQUEADO,
  MENSAGEM_CSV_SEM_ITENS,
} from 'helper/fluxo-aprovacao-pedido-helper';
import { FluxoAprovacaoPedidoUseCase, ItemBusca, SolicitacaoItem } from 'service/api/rest/fluxo-aprovacao-pedido-usecase';
import { ServiceOperationResult } from 'service/api/rest/rest-usecase';
import { PedidoResumo } from '../../../@types/entity/pedido-resumo-entity';
import { PedidoDetalhe } from '../../../@types/entity/pedido-detalhe-entity';
import { FluxoAprovacaoPedidoEntity } from '../../../@types/entity/fluxo-aprovacao-pedido-entity';
import { AbaPedido, FluxoAprovacaoPedidoListaPage } from 'pages/layout/analysis/fluxo-aprovacao-pedido-lista-page';
import { FluxoAprovacaoPedidoModalDetalhe } from 'pages/layout/analysis/components/fluxo-aprovacao-pedido-modal-detalhe';
import ErrorNotAuthorizedPage from 'pages/layout/error/error-not-authorized-page';
import { hasAcessoFluxoAprovacaoPedido } from 'helper/role-helper';

/** Período padrão do filtro: do mês anterior até hoje (mesma janela do legado). */
function periodoPadrao(): { inicio: string; fim: string } {
  const hoje = new Date();
  const inicio = new Date();
  inicio.setMonth(hoje.getMonth() - 1);
  return { inicio: ParseDateToEn(inicio.toString()), fim: ParseDateToEn(hoje.toString()) };
}

/** Dispara o download de um arquivo no navegador a partir do conteúdo em memória. */
function baixarArquivo(conteudo: BlobPart, nome: string, tipo: string): void {
  const url = window.URL.createObjectURL(new Blob([conteudo], { type: tipo }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nome);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
}

/**
 * Tela nova "Aprovação de Pedidos" (rota /fluxo-aprovacao-pedido). Autocontida e usada
 * apenas quando a empresa tem a flag ligada: as três abas (Analítico, Sintético e Campanhas —
 * FU01/FU02) consomem o MESMO fluxo novo (/fluxo-aprovacao-pedido/...), separadas pela origem do
 * pedido, com o mesmo comportamento (abrir detalhe, aprovar/validar/editar/reprovar/gerar e CSV).
 */
type GeracaoAutomatica = 'ignorada' | 'gerada' | 'falhou';

export const FluxoAprovacaoPedidoListaFactory: React.FC = () => {
  const ENDPOINT = getEnv('REACT_APP_END_POINT_FLUXO_APROVACAO_PEDIDO');

  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = UseAlert();
  const { loggedUserData } = UseAuth();

  const service = useMemo(() => new FluxoAprovacaoPedidoUseCase(ENDPOINT!), [ENDPOINT]);
  const padrao = useMemo(periodoPadrao, []);

  const [showScreen, setShowScreen] = useState<boolean>(false);
  const [dataInicio, setDataInicio] = useState<string>(padrao.inicio);
  const [dataFim, setDataFim] = useState<string>(padrao.fim);
  const [analiticos, setAnaliticos] = useState<PedidoResumo[]>([]);
  const [sinteticos, setSinteticos] = useState<PedidoResumo[]>([]);
  const [campanhas, setCampanhas] = useState<PedidoResumo[]>([]);
  const [detalheAberto, setDetalheAberto] = useState<PedidoDetalhe | null>(null);
  const [nomeDetalhe, setNomeDetalhe] = useState<string | undefined>(undefined);
  const [showDetalhe, setShowDetalhe] = useState<boolean>(false);

  useEffect(() => {
    if (hasAcessoFluxoAprovacaoPedido(loggedUserData?.role)) {
      setShowScreen(true);
    }
  }, [loggedUserData]);

  // FU01/FU02: as três abas vêm do MESMO endpoint do fluxo novo (listarPedidos),
  // separadas pela origem do pedido; pedidos sem origem (anteriores ao patch) caem no Analítico.
  const carregar = useCallback(async () => {
    showLoader();
    try {
      const result = await service.listarPedidos();
      if (!result.success) {
        showErrorAlert(showAlert, resolverMensagemErro(result, 'Não foi possível listar os pedidos.'));
        return;
      }
      const pedidos = filtrarPorPeriodo(result.data ?? [], dataInicio, dataFim);
      setSinteticos(pedidos.filter((p) => p.origem === 'SINTETICO'));
      setCampanhas(pedidos.filter((p) => p.origem === 'CAMPANHAS'));
      setAnaliticos(pedidos.filter((p) => p.origem !== 'SINTETICO' && p.origem !== 'CAMPANHAS'));
    } finally {
      hideLoader();
    }
  }, [service, showAlert, dataInicio, dataFim, showLoader, hideLoader]);

  useEffect(() => {
    if (showScreen) {
      carregar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showScreen]);

  /**
   * CSV do fluxo novo (todas as abas): o conteúdo vem do backend, no mesmo layout do pedido do Analítico/Sintético.
   * Só baixa se o pedido estiver aprovado. A lista já desabilita o botão; aqui a regra é
   * reavaliada com o status FRESCO do detalhe, que também cobre a lista defasada (o pedido pode ter
   * mudado de status desde a última carga).
   *
   */
  async function baixarCsv(_aba: AbaPedido, id: number): Promise<void> {
    showLoader();
    showWaitAlert(showAlert, 'Aguarde um momento');
    try {
      const result = await service.carregarDetalhe(id);
      if (!result.success || !result.data) {
        showErrorAlert(showAlert, resolverMensagemErro(result, 'Não foi possível carregar o pedido.'));
        return;
      }
      const detalhe = result.data;
      if (!podeBaixarCsv(detalhe.status)) {
        showErrorAlert(showAlert, MENSAGEM_CSV_BLOQUEADO);
        return;
      }
      if ((detalhe.itens ?? []).length === 0) {
        showErrorAlert(showAlert, MENSAGEM_CSV_SEM_ITENS);
        return;
      }
      const csv = await service.exportarCsv(id);
      if (!csv.success || !csv.data) {
        showErrorAlert(showAlert, resolverMensagemErro(csv, 'Não foi possível exportar o pedido.'));
        return;
      }
      baixarArquivo(csv.data, `Pedido_${id}.csv`, 'text/csv');
      showSuccessAlert(showAlert, 'CSV gerado com sucesso');
    } finally {
      hideLoader();
    }
  }

  // --- Detalhe do pedido em modal + ações do fluxo. ---

  async function abrirDetalhe(idPedido: number): Promise<void> {
    showLoader();
    const result = await service.carregarDetalhe(idPedido);
    hideLoader();
    if (result.success && result.data) {
      setDetalheAberto(result.data);
      setNomeDetalhe([...analiticos, ...sinteticos, ...campanhas].find((pedido) => pedido.idPedido === idPedido)?.nome);
      setShowDetalhe(true);
      return;
    }
    showErrorAlert(showAlert, resolverMensagemErro(result, 'Não foi possível carregar o pedido.'));
  }

  function fecharDetalhe(): void {
    setShowDetalhe(false);
    setDetalheAberto(null);
    setNomeDetalhe(undefined);
  }

  function tratarErro(result: ServiceOperationResult<unknown>, msgErro: string): void {
    const fallback = isConflito(result.statusCode) ? MENSAGEM_BLOQUEIO_PADRAO : msgErro;
    showAlertPorStatus(showAlert, resolverMensagemErro(result, fallback), result.statusCode);
  }

  /** Ações que PASSAM A VEZ fecham o modal e voltam para a lista (recarregada). */
  async function fecharERecarregar(): Promise<void> {
    fecharDetalhe();
    await carregar();
  }

  async function recarregarDetalhe(idPedido: number): Promise<void> {
    const result = await service.carregarDetalhe(idPedido);
    if (result.success && result.data) {
      setDetalheAberto(result.data);
      return;
    }
    showErrorAlert(showAlert, resolverMensagemErro(result, 'Não foi possível carregar o pedido.'));
  }

  /** Se a aprovação/validação liberou a geração, gera o pedido automaticamente. */
  async function gerarSeLiberado(entity?: FluxoAprovacaoPedidoEntity | null): Promise<GeracaoAutomatica> {
    if (!geracaoLiberadaApos(entity)) {
      return 'ignorada';
    }
    const result = await service.gerarPedido(entity!.idPedido);
    if (!result.success) {
      tratarErro(result, 'Pedido aprovado, mas não foi possível gerar automaticamente.');
      return 'falhou';
    }
    return 'gerada';
  }

  async function aprovarOuValidar(
    promise: Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>>,
    msgErro: string,
    msgOk: string
  ): Promise<void> {
    showLoader();
    const result = await promise;
    if (!result.success) {
      hideLoader();
      tratarErro(result, msgErro);
      return;
    }
    const geracao = await gerarSeLiberado(result.data);
    hideLoader();
    if (geracao === 'falhou') {
      await fecharERecarregar();
      return;
    }
    showSuccessAlert(showAlert, geracao === 'gerada' ? 'Pedido aprovado e gerado com sucesso.' : result.message || msgOk);
    await fecharERecarregar();
  }

  async function acaoSimples(
    promise: Promise<ServiceOperationResult<FluxoAprovacaoPedidoEntity>>,
    msgErro: string,
    msgOk: string
  ): Promise<void> {
    showLoader();
    const result = await promise;
    hideLoader();
    if (!result.success) {
      tratarErro(result, msgErro);
      return;
    }
    showSuccessAlert(showAlert, result.message || msgOk);
    await fecharERecarregar();
  }

  async function acaoMantendoModal(
    promise: Promise<ServiceOperationResult<unknown>>,
    msgErro: string,
    msgOk: string,
    idPedido: number
  ): Promise<void> {
    showLoader();
    const result = await promise;
    if (!result.success) {
      hideLoader();
      tratarErro(result, msgErro);
      return;
    }
    await recarregarDetalhe(idPedido);
    await carregar();
    hideLoader();
    showSuccessAlert(showAlert, result.message || msgOk);
  }

  const handleAprovar = (id: number) =>
    aprovarOuValidar(service.aprovar(id), 'Não foi possível aprovar o pedido.', 'Pedido aprovado com sucesso.');
  const handleValidar = (id: number) =>
    // Para o usuário é "Aprovar" nos dois turnos; o endpoint segue sendo `validar`.
    aprovarOuValidar(service.validar(id), 'Não foi possível aprovar o pedido.', 'Pedido aprovado com sucesso.');
  const handleReprovar = (id: number, justificativa: string) =>
    acaoSimples(service.reprovar(id, justificativa), 'Não foi possível reprovar o pedido.', 'Pedido reprovado.');
  const handleEditar = (id: number, solicitacoes: SolicitacaoItem[], justificativa: string) =>
    acaoSimples(
      service.editarPedido(id, solicitacoes, justificativa),
      'Não foi possível enviar as solicitações.',
      'Solicitações enviadas para a contraparte.'
    );
  /**
   * Decidir não aprova nem gera. Aceitar todas as edições só libera o botão Aprovar;
   * a geração acontece no clique, e só quando não houve recusa nem edição nova.
   */
  const handleDecidir = async (id: number, idsAceitas: number[], idsRecusadas: number[]) => {
    showLoader();
    const result = await service.decidirSolicitacoes(id, idsAceitas, idsRecusadas);
    if (!result.success) {
      hideLoader();
      tratarErro(result, 'Não foi possível decidir as solicitações.');
      return;
    }
    await recarregarDetalhe(id);
    await carregar();
    hideLoader();
    showSuccessAlert(showAlert, result.message || 'Solicitações processadas com sucesso.');
  };
  /** Retirar mantém o modal aberto e recarrega o detalhe (não passa a vez). */
  const handleRetirar = (id: number, idSolicitacao: number) =>
    acaoMantendoModal(
      service.retirarSolicitacao(id, idSolicitacao),
      'Não foi possível retirar a solicitação.',
      'Solicitação retirada.',
      id
    );
  const handleEnviar = (id: number) =>
    acaoSimples(service.enviarParaAprovacao(id), 'Não foi possível enviar o pedido.', 'Pedido enviado para aprovação com sucesso.');
  const handleGerar = (id: number) =>
    acaoSimples(service.gerarPedido(id), 'Não foi possível gerar o pedido.', 'Pedido gerado com sucesso.');

  /** Busca itens da filial do pedido aberto para o typeahead da edição. */
  const buscarItensDisponiveis = useCallback(
    async (termo: string): Promise<ItemBusca[]> => {
      if (!detalheAberto) {
        return [];
      }
      const result = await service.buscarItens(detalheAberto.idPedido, termo);
      return result.data ?? [];
    },
    [service, detalheAberto]
  );

  /** Carrega TODAS as versões do pedido (cada uma com os seus itens) para a modal. */
  async function carregarVersoes(idPedido: number) {
    const result = await service.carregarVersoes(idPedido);
    if (!result.success) {
      showErrorAlert(showAlert, resolverMensagemErro(result, 'Não foi possível carregar as versões do pedido.'));
      return [];
    }
    return result.data ?? [];
  }

  return (
    <>
      {showScreen ? (
        <>
          <div className="mx-5">
            <TitleGroupComponent titlePage="Aprovação de Pedidos">
              <Row></Row>
            </TitleGroupComponent>
            <div className="mx-3">
              <FluxoAprovacaoPedidoListaPage
                analiticos={analiticos}
                sinteticos={sinteticos}
                campanhas={campanhas}
                dataInicio={dataInicio}
                dataFim={dataFim}
                onDataInicio={setDataInicio}
                onDataFim={setDataFim}
                onFiltrar={carregar}
                onAbrir={abrirDetalhe}
                onBaixarCsv={baixarCsv}
                onCarregarVersoes={carregarVersoes}
              />
            </div>
          </div>
          <FluxoAprovacaoPedidoModalDetalhe
            show={showDetalhe}
            detalhe={detalheAberto}
            nome={nomeDetalhe}
            onFechar={fecharDetalhe}
            onEnviar={handleEnviar}
            onAprovar={handleAprovar}
            onValidar={handleValidar}
            onReprovar={handleReprovar}
            onEditar={handleEditar}
            onDecidir={handleDecidir}
            onRetirar={handleRetirar}
            onGerar={handleGerar}
            onBuscarItens={buscarItensDisponiveis}
          />
          <Footer />
        </>
      ) : (
        <ErrorNotAuthorizedPage />
      )}
    </>
  );
};

export default FluxoAprovacaoPedidoListaFactory;
